import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Zap, Loader2, CheckCircle2, Sparkles, Timer } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FIELD_LABELS = { picture: "Profilbild", cover: "Titelbild", bio: "Über mich", location: "Wohnort", birthday: "Geburtstag" };

function Missing({ fields, prefix, id }) {
  if (!fields?.length) return null;
  return (
    <div className="space-y-1.5" data-testid={`${prefix}task-missing-${id}`}>
      <p className="text-[11px] font-semibold text-white/85">Noch {fields.length === 1 ? "1 Angabe fehlt" : `${fields.length} Angaben fehlen`}:</p>
      <div className="flex flex-wrap gap-1.5">
        {fields.map((f) => <Link key={f} to="/profil" data-testid={`${prefix}task-missing-${id}-${f}`} className="px-2.5 py-1 rounded-full bg-white/90 text-amber-700 text-[11px] font-bold hover:bg-white transition-colors">+ {FIELD_LABELS[f] || f}</Link>)}
      </div>
    </div>
  );
}

const pad = (n) => String(n).padStart(2, "0");

function Countdown({ to, prefix, id, onEnd }) {
  const [ms, setMs] = useState(() => new Date(to) - Date.now());
  useEffect(() => {
    const t = setInterval(() => { const m = new Date(to) - Date.now(); setMs(m); if (m <= 0) { clearInterval(t); onEnd(); } }, 1000);
    return () => clearInterval(t);
  }, [to, onEnd]);
  const s = Math.max(0, Math.floor(ms / 1000)), d = Math.floor(s / 86400);
  const urgent = s < 3600 * 6;
  return (
    <p className={`flex items-center gap-1.5 text-xs font-bold tabular-nums ${urgent ? "text-white" : "text-white/85"}`} data-testid={`${prefix}task-countdown-${id}`}>
      <Timer className={`w-3.5 h-3.5 ${urgent ? "animate-pulse" : ""}`} /> Endet in {d > 0 && `${d} ${d === 1 ? "Tag" : "Tage"} `}{pad(Math.floor((s % 86400) / 3600))}:{pad(Math.floor((s % 3600) / 60))}:{pad(s % 60)}
    </p>
  );
}

function TaskRow({ t, prefix, onClaimed }) {
  const [busy, setBusy] = useState(false);
  const { refreshUser } = useAuth();
  const claim = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/tasks/${t.id}/claim`); toast.success(`Aufgabe erledigt! +${data.earned} Punkte ⚡`); window.dispatchEvent(new Event("points-changed")); refreshUser(); onClaimed(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <li className="rounded-2xl bg-white/15 p-3 space-y-2" data-testid={`${prefix}task-${t.id}`}>
      <div className="flex items-start gap-2">
        <Link to={t.url} className="flex-1 min-w-0 font-bold leading-snug hover:underline">{t.title}</Link>
        <span className="shrink-0 px-2 py-0.5 rounded-full bg-white text-amber-600 text-xs font-extrabold">+{t.points}</span>
      </div>
      {t.goal > 1 && (
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 rounded-full bg-white/25 overflow-hidden"><div className="h-full bg-white rounded-full" style={{ width: `${(t.progress / t.goal) * 100}%` }} /></div>
          <span className="text-xs font-bold">{t.progress}/{t.goal}</span>
        </div>
      )}
      {!t.claimed && !t.done && <Missing fields={t.missing} prefix={prefix} id={t.id} />}
      {t.claimed
        ? <p className="text-xs font-bold flex items-center gap-1" data-testid={`${prefix}task-claimed-${t.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Punkte abgeholt</p>
        : t.done
          ? <button onClick={claim} disabled={busy} data-testid={`${prefix}task-claim-${t.id}`} className="w-full h-9 rounded-full bg-white text-amber-600 font-bold text-sm flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} +{t.points} Punkte abholen</button>
          : <Link to={t.url} data-testid={`${prefix}task-go-${t.id}`} className="block text-center w-full h-9 leading-9 rounded-full border border-white/60 font-bold text-sm">Jetzt erledigen</Link>}
      {t.ends_at && !t.claimed && <Countdown to={t.ends_at} prefix={prefix} id={t.id} onEnd={onClaimed} />}
    </li>
  );
}

export function TasksWidget({ prefix = "" }) {
  const [list, setList] = useState([]);
  const load = useCallback(() => api.get("/tasks").then(({ data }) => setList(data)).catch(() => {}), []);
  useEffect(() => {
    load();
    window.addEventListener("points-changed", load);
    return () => window.removeEventListener("points-changed", load);
  }, [load]);
  const open = list.filter((t) => !t.claimed);
  if (!open.length) return null;
  return (
    <div className="relative overflow-hidden rounded-3xl p-5 text-white bg-gradient-to-br from-amber-400 via-orange-400 to-pink-500 shadow-lg shadow-orange-200 space-y-3" data-testid={`${prefix}tasks-widget`}>
      <span className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/10" />
      <p className="relative text-[11px] uppercase tracking-widest font-bold text-white/90 flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" /> Spontane Aufgaben</p>
      <ul className="relative space-y-2">{open.map((t) => <TaskRow key={t.id} t={t} prefix={prefix} onClaimed={load} />)}</ul>
    </div>
  );
}
